import { inject, Injectable } from '@angular/core';
import { RootServicesModule } from './root-services.module';
import { IFile, ISCDUser, IFormData } from '@app-root-interfaces';
import { HttpClient } from '@angular/common/http';
import { environment } from '@app-environment';
import { Observable, of } from 'rxjs';
import { map, tap, switchMap } from 'rxjs/operators';
import { IPermissionOptions } from '../interfaces/permission-options.interface';
import { TinyHelper } from '../../shared/helper/tiny-helper';
import { ProfileAddressHelper } from '../../shared/helper/profile-address.helper';
import { downloadBlob } from '../../shared/helper/download.helper';
import { ProfileHelper } from '../../shared/helper/profile.helper';
import { DownloadResponseDto, ManagementDto, SignedURLDto } from '@scf-monorepo/backend-lib/dtos';
import { IProfile, ISignedURL } from '@scf-monorepo/interfaces';
import { HttpHeaders } from '@angular/common/http';

@Injectable({
    providedIn: RootServicesModule
})
export class DownloadService {
    private readonly http = inject(HttpClient);

    public downloadFile(file: IFile): Observable<ArrayBuffer> {
        const fileName = file.name || file.fileName;
        if (!fileName) {
            throw new Error('File name is required for download');
        }
        return this.http
            .get<SignedURLDto>(`${environment.gatewayAPI}/file/${fileName}/download`)
            .pipe(switchMap(({ signedURL }) => this.http.get(signedURL, { responseType: 'arraybuffer' })));
    }

    public downloadBase64File(file: IFile): Observable<ArrayBuffer> {
        const fileName = file.name || file.fileName;
        if (!fileName) {
            throw new Error('File name is required for download');
        }
        return this.http
            .get<string>(`${environment.gatewayAPI}/file/${fileName}/download`, {
                headers: new HttpHeaders({
                    Accept: 'application/octet-stream'
                })
            })
            .pipe(map((file) => this.base64ToArrayBuffer(file)));
    }

    private base64ToArrayBuffer(base64: string): ArrayBuffer {
        const binaryString = window.atob(base64);
        const len = binaryString.length;
        const bytes = new Uint8Array(len);
        for (let i = 0; i < len; i++) {
            bytes[i] = binaryString.charCodeAt(i);
        }
        return bytes.buffer;
    }

    /**
     * Downloads a file after filling it with provided data.
     *
     * This method sends a POST request to the server with the provided data and returns Blob object
     * representing the filled file.
     *
     * @param {IFile} file - The file object that needs to be filled and downloaded.
     * @param {Object} formData - An object containing the snippet data.
     * @param {IProfile} profile - The user's profile object containing user-specific information.
     * @param {string} language - The language code to be used in the processing of the file.
     * @returns {Observable<{ data: ArrayBuffer; fileNameSuffix: string }>} - An observable emitting
     *          an object containing the filled file as a ArrayBuffer and a custom file name suffix.
     */
    public downloadFilledFile(
        file: IFile,
        formData: { [key: string]: string | Array<string> },
        profile: IProfile,
        language: string
    ): Observable<{ data: ArrayBuffer; fileNameSuffix: string; emailMessage?: string } | string> {
        const data = {
            ...this.convertSpecialCharacters({ ...formData }),
            ...this.convertProfileToTemplateModel(profile, language),
            ...this.convertCustomFormDataEntries({ ...formData }),
            ...this.addSenderLabels(profile, language)
        };

        return this.http
            .post<SignedURLDto | DownloadResponseDto>(
                `${environment.gatewayAPI}/download/fill/${file.favoriteId}`,
                data
            )
            .pipe(
                switchMap((response) => {
                    if ('message' in response) {
                        if (response.signedURL) {
                            return this.http.get(response.signedURL, { responseType: 'arraybuffer' }).pipe(
                                map((buffer) => ({
                                    data: buffer,
                                    fileNameSuffix: this.customFileName(data, file),
                                    emailMessage: response.message
                                }))
                            );
                        }
                        return of(response.message);
                    } else {
                        return this.http
                            .get(response.signedURL, { responseType: 'arraybuffer' })
                            .pipe(map((buffer) => ({ data: buffer, fileNameSuffix: this.customFileName(data, file) })));
                    }
                })
            );
    }

    // List of favorite IDs for which the custom file name should only include the offer number and a fixed suffix.
    private readonly SIO_FAVORITE_IDS = [
        'cd162cba-1301-4737-99ee-761a151a116d', // SIO english
        'cb27c962-065d-426c-981f-8aa2820d9427' // SIO deutsch
    ];

    private customFileName(formData: IFormData, file: IFile): string {
        const offerNumberKey = Object.keys(formData).find((snippet) => snippet.startsWith('scf_offerNumber_custom'));

        if (!formData[offerNumberKey]) {
            return '';
        }

        const offerNumber = formData[offerNumberKey];

        if (this.SIO_FAVORITE_IDS.includes(file.favoriteId)) {
            return `${offerNumber}_SN29500`;
        }

        const offerTitleKey = Object.keys(formData).find((snippet) => snippet.startsWith('scf_angebotstitel_text'));
        const title = formData[offerTitleKey]?.[0]?.replaceAll(' ', '_') ?? '';

        return `${offerNumber}_${title}`;
    }

    private convertCustomFormDataEntries(formData: { [key: string]: string | string[] }): { [key: string]: [string] } {
        const offerTitleKey = Object.keys(formData).find((snippet) => snippet.startsWith('scf_angebotstitel_text'));

        if (!offerTitleKey) {
            return;
        }

        let offerTitle = formData[offerTitleKey];
        if (Array.isArray(offerTitle)) {
            offerTitle = offerTitle.join(' ');
        }

        return { [offerTitleKey]: [offerTitle] };
    }

    private convertSpecialCharacters(formData: IFormData): IFormData {
        Object.keys(formData).forEach((key) => {
            const value = formData[key];
            if (typeof value === 'string') {
                formData[key] = this.replaceCharacters(value);
            } else if (Array.isArray(value)) {
                const specialCharacter = ['(#10)', '~', '|', '\n'];
                if (
                    value.length === 1 &&
                    typeof value[0] === 'string' &&
                    specialCharacter.some((v) => value[0].includes(v))
                ) {
                    formData[key] = this.replaceCharacters(value[0]);
                } else {
                    formData[key] = value.map((item) => {
                        if (typeof item === 'string') {
                            return this.replaceCharacters(item);
                        } else {
                            return item;
                        }
                    });
                }
            }
        });
        return formData;
    }

    /**
     * Adds sender labels based on the user's profile.
     * @param profile The user's profile.
     * @param language The language code to be used for the translation.
     * @returns An object containing the sender labels.
     */
    private addSenderLabels(profile: IProfile, language: string) {
        return {
            countrylong: TinyHelper.getCountryLong(profile.country, language) || ''
        };
    }

    private replaceCharacters(value: string): string {
        return value
            .replace(/\(#10\)/g, '\n') // new line
            .replace(/~/g, '\xA0') // protected space
            .replace(/\|/g, '\x0a'); // line break
    }

    public getPermissionOptions(): Observable<IPermissionOptions> {
        return this.http.get<ManagementDto[]>(`${environment.gatewayAPI}/information/management`).pipe(
            map((data) =>
                data.reduce(
                    (acc, info) => {
                        acc.country.push(info.country);
                        acc.company.push(info.organisation);
                        acc.orgunit.push(info.orgUnit);
                        return acc;
                    },
                    { country: [], company: [], orgunit: [] }
                )
            ),
            map((data) => {
                data.country = data.country.filter((item) => !!item).filter(TinyHelper.unique);
                data.company = data.company.filter((item) => !!item).filter(TinyHelper.unique);
                data.orgunit = data.orgunit.filter((item) => !!item).filter(TinyHelper.unique);
                return data;
            })
        );
    }

    public downloadTranslationInformation(): Observable<Blob> {
        return this.http.get<ISignedURL>(`${environment.gatewayAPI}/translations/download`).pipe(
            switchMap(({ signedURL }) => this.http.get(signedURL, { responseType: 'blob' })),
            tap((blob) => {
                downloadBlob(blob, 'translations.csv');
            })
        );
    }

    private convertProfileToTemplateModel(profile: IProfile, language: string) {
        const mobile = profile.phone ? profile.mobile : '';
        const scdUser = profile.type === 'COPIED' ? this.createCustomizedScdUser(profile) : profile.scdUser;
        const salutation = ProfileAddressHelper.getSalutation(scdUser, language);

        // the SCF_PRO fields are still here because of downwards compatibility, can hopefully be removed someday
        return {
            Title: profile.displayName,
            SCF_PRO_User: profile.mail,
            SCF_PRO_countryName: profile.country,
            SCF_PRO_organizationName: profile.organization,
            SCF_PRO_ouNameShort: profile.scdUser.organizationUnit ?? '',
            SCF_PRO_localityName: profile.location,
            SCF_PRO_phoneNumber: profile.phone || profile.mobile,
            SCF_PRO_faxNumber: ProfileHelper.formatPhoneNumber(profile.scdUser.fax) ?? '',
            SCF_PRO_tcgid: profile.gid,
            SCF_PRO_surname: profile.surName,
            SCF_PRO_givenName: profile.scdUser.administrativeGivenNameInt || profile.firstName,
            SCF_PRO_surnameNational: profile.scdUser.surNameNat ?? '',
            SCF_PRO_givenNameNational: profile.scdUser.administrativeGivenNameNat || profile.scdUser.firstNameNat || '',
            SCF_PRO_personalTitle: profile.scdUser.personalTitle ?? '',
            SCF_PRO_mail: profile.mail,
            SCF_PRO_department: profile.department.split(' ').join('\xA0'), // use nonbreaking spaces
            SCF_PRO_departmentText: profile.scdUser.departmentText ?? '',
            SCF_PRO_building: profile.scdUser.building ?? '',
            SCF_PRO_roomNumber: profile.scdUser.room ?? '',
            SCF_PRO_nickName: profile.scdUser.nickName ?? '',
            SCF_PRO_telephonenumber: profile.phone,
            SCF_PRO_alternatePhone: profile.scdUser.alternatePhone ?? '',
            SCF_PRO_personalFax: ProfileHelper.formatPhoneNumber(profile.scdUser.personalFax) ?? '',
            SCF_PRO_secretary: profile.scdUser.secretary ?? '',
            SCF_PRO_costLocation: profile.scdUser.costLocation ?? '',
            SCF_PRO_costLocationUnit: profile.scdUser.costLocationUnit ?? '',
            SCF_PRO_personalIdNumber: profile.scdUser.personalIdNumber ?? '',
            SCF_PRO_status: profile.scdUser.status ?? '',
            SCF_PRO_orgId: profile.scdUser.orgId ?? '',
            SCF_PRO_mobileTelephoneNumber: mobile ?? '',
            SCF_PRO_Salutation: salutation ?? '',

            scd_user: profile.mail,
            scd_countryName: profile.country,
            scd_organizationName: profile.organization,
            scd_ouNameShort: profile.scdUser.organizationUnit ?? '',
            scd_localityName: profile.location,
            scd_phoneNumber: profile.phone || profile.mobile,
            scd_faxNumber: ProfileHelper.formatPhoneNumber(profile.scdUser.fax) ?? '',
            scd_tcgid: profile.gid,
            scd_surname: profile.surName,
            scd_givenName: profile.scdUser.administrativeGivenNameInt || profile.firstName,
            scd_surnameNational: profile.scdUser.surNameNat ?? '',
            scd_givenNameNational: profile.scdUser.administrativeGivenNameNat || profile.scdUser.firstNameNat || '',
            scd_personalTitle: profile.scdUser.personalTitle ?? '',
            scd_mail: profile.mail,
            scd_department: profile.department.split(' ').join('\xA0'), // use nonbreaking spaces
            scd_department_excel: profile?.department?.split(' ').join('\xA0').replace('&', '\u200B&&\u200B') ?? '', // replace $ with && for Excel compatibility
            scd_departmentText: profile.scdUser.departmentText ?? '',
            scd_building: profile.scdUser.building ?? '',
            scd_roomNumber: profile.scdUser.room ?? '',
            scd_nickName: profile.scdUser.nickName ?? '',
            scd_telephonenumber: profile.phone,
            scd_alternatePhone: profile.scdUser.alternatePhone ?? '',
            scd_personalFax: ProfileHelper.formatPhoneNumber(profile.scdUser.personalFax) ?? '',
            scd_secretary: profile.scdUser.secretary ?? '',
            scd_costLocation: profile.scdUser.costLocation ?? '',
            scd_costLocationUnit: profile.scdUser.costLocationUnit ?? '',
            scd_personalIdNumber: profile.scdUser.personalIdNumber ?? '',
            scd_status: profile.scdUser.status ?? '',
            scd_orgId: profile.scdUser.orgId ?? '',
            scd_mobileTelephoneNumber: mobile ?? '',
            scd_salutation: salutation ?? '',
            scd_function: profile.function ?? '',
            scd_initials: profile.initials ?? ''
        };
    }

    private createCustomizedScdUser(profile: IProfile): ISCDUser {
        // Copies the profile names into all scdUser name fields.
        return {
            ...profile.scdUser,
            nickName: profile.firstName,
            firstNameNat: profile.firstName,
            firstName: profile.firstName,
            administrativeGivenNameInt: profile.firstName,
            administrativeGivenNameNat: profile.firstName,
            surName: profile.surName,
            surNameNat: profile.surName,
            preferredSN: profile.surName
        } as ISCDUser;
    }
}
